/**
 * User-Progress API client — guide-api /api/v1/progress/*
 *
 * Backend spec: Content-Post #1275 (GuideDevBot design).
 * Live endpoint contract confirmed via IACP 2026-07-01.
 *
 * Anonymous by design — `X-Device-Id` header is generated once per
 * browser and lives in localStorage (getDeviceId in ./submissions.ts).
 * On Google-Login the caller calls `mergeProgress()` with a bearer
 * token to link the anonymous device-events to the user account.
 *
 * Everything is fire-and-forget from the caller's perspective — the
 * offline queue in `progressTracker.ts` retries failed appends so a
 * single dropped request doesn't lose the event.
 */

import { getDeviceId } from './submissions'

const API_BASE = 'https://guide-api.arkserver.arkturian.com/api/v1/progress'

export type ProgressEventKind =
  | 'species_found'
  | 'poi_visited'
  | 'annotation_heard'
  | 'chat_session_ended'
  | 'photo_submitted'

export interface ProgressEventInput {
  kind: ProgressEventKind
  /** For species_found / annotation_heard — knowledge-api id. */
  knowledge_id?: number | null
  /** For poi_visited — ArTrack waypoint id. */
  waypoint_id?: number | null
  /** Denormalized human-readable name — write-time so /summary stays
   *  offline-cheap (no knowledge-api roundtrip per event on aggregate).
   *  For species: hit.name from the tool result. For POI: station name. */
  label?: string | null
  /** kind-specific extras: {source, distance_m, duration_s, cost_eur,
   *  turn_count, session_id, annotation_id, km, played_ms, ...}. */
  payload?: Record<string, unknown> | null
  /** Client-generated UUID for idempotency. Retries with the same
   *  client_event_id become no-ops on the server (deduped=true). */
  client_event_id: string
  /** ISO-8601 event time from the client — may differ from
   *  created_at when replaying queued offline events. */
  occurred_at?: string
}

export interface ProgressEventAck {
  id: number
  kind: ProgressEventKind
  /** true when the server saw this client_event_id before and skipped. */
  deduped: boolean
}

export interface ProgressSummary {
  text: string
  has_progress: boolean
}

export interface ProgressState {
  found_species: Array<{ knowledge_id: number; label: string | null; count: number }>
  visited_pois: Array<{ waypoint_id: number; label: string | null; count: number }>
  km_walked: number
  counts: Record<ProgressEventKind, number>
  timeline: Array<{
    kind: ProgressEventKind
    knowledge_id: number | null
    waypoint_id: number | null
    label: string | null
    at: string
  }>
}

function headers(): HeadersInit {
  return {
    'content-type': 'application/json',
    'X-Device-Id': getDeviceId(),
  }
}

/** Send one event. Rejects on network error / non-2xx — the tracker
 *  catches and buffers. Idempotent via client_event_id. */
export async function postProgressEvent(event: ProgressEventInput): Promise<ProgressEventAck> {
  const res = await fetch(`${API_BASE}/events`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(event),
  })
  if (!res.ok) throw new Error(`progress/events ${res.status}: ${await res.text().catch(() => '')}`)
  return res.json()
}

/** Bulk-append — used by the offline queue flush. Server dedupes per
 *  client_event_id, so replaying the same batch is safe. */
export async function postProgressEventsBulk(events: ProgressEventInput[]): Promise<{
  accepted: number
  deduped: number
}> {
  const res = await fetch(`${API_BASE}/events/bulk`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ events }),
  })
  if (!res.ok) throw new Error(`progress/events/bulk ${res.status}: ${await res.text().catch(() => '')}`)
  return res.json()
}

/** Aggregated state — used by dev tools / the future Tagebuch page. */
export async function getProgress(): Promise<ProgressState> {
  const res = await fetch(`${API_BASE}/`, { headers: headers() })
  if (!res.ok) throw new Error(`progress ${res.status}`)
  return res.json()
}

/** Compact German text block for Realtime-prompt injection — analog to
 *  the Bus-Digest. `has_progress=false` means the server returned a
 *  neutral placeholder; caller can choose to skip injection then. */
export async function getProgressSummary(signal?: AbortSignal): Promise<ProgressSummary> {
  const res = await fetch(`${API_BASE}/summary`, { headers: headers(), signal })
  if (!res.ok) throw new Error(`progress/summary ${res.status}`)
  return res.json()
}

/** Login-Merge: link all anonymous device_id events to the JWT's
 *  user_id. Caller supplies the JWT. Server reads user from bearer,
 *  device from header, sets user_id on all matching rows. */
export async function mergeProgress(jwt: string): Promise<{ merged: number; user_id: string }> {
  const res = await fetch(`${API_BASE}/merge`, {
    method: 'POST',
    headers: {
      ...headers(),
      Authorization: `Bearer ${jwt}`,
    },
  })
  if (!res.ok) throw new Error(`progress/merge ${res.status}: ${await res.text().catch(() => '')}`)
  return res.json()
}
